import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/SEOHead';
import { Shield, Lock, Eye, FileText, Users, Database, Globe, Mail, AlertCircle } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const seoConfig = {
    title: 'Privacy Policy | WEQASA',
    description: 'Informativa sulla privacy e trattamento dei dati personali di WEQASA. Scopri come proteggiamo i tuoi dati in conformità al GDPR.',
    keywords: 'privacy policy weqasa, gdpr, protezione dati personali, trattamento dati'
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead {...seoConfig} />
      <Header />

      {/* Hero Section */}
      <section className="bg-[#F5F5F7] py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-16 h-16 bg-[#0071e3]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-[#0071e3]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-semibold text-[#1D1D1F] tracking-tight mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-[#86868B]">
            Informativa ai sensi del Regolamento UE 2016/679 (GDPR)
          </p>
          <p className="text-sm text-[#86868B] mt-2">
            Ultimo aggiornamento: Dicembre 2024
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            
            {/* Titolare */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">1. Titolare del Trattamento</h2>
              <div className="bg-[#F5F5F7] rounded-xl p-6">
                <p className="text-[#424245] leading-relaxed">
                  <strong>WEQASA S.r.l.</strong><br />
                  Sede legale: Via Roma 123, 00100 Roma (RM)<br />
                  P.IVA: 12345678901<br />
                  Email: <a href="mailto:privacy@weqasa.it" className="text-[#0071e3] hover:underline">privacy@weqasa.it</a><br />
                  PEC: weqasa@pec.it
                </p>
              </div>
            </div>

            {/* DPO */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">2. Responsabile della Protezione dei Dati (DPO)</h2>
              <p className="text-[#424245] leading-relaxed">
                WEQASA ha nominato un Responsabile della Protezione dei Dati (Data Protection Officer) 
                contattabile all'indirizzo: <a href="mailto:dpo@weqasa.it" className="text-[#0071e3] hover:underline">dpo@weqasa.it</a>
              </p>
            </div>

            {/* Dati Raccolti */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">3. Categorie di Dati Raccolti</h2>
              
              <h3 className="text-xl font-medium text-[#1D1D1F] mb-3">3.1 Dati forniti volontariamente</h3>
              <ul className="space-y-2 text-[#424245] mb-6">
                <li>• <strong>Dati identificativi:</strong> nome, cognome, codice fiscale, data di nascita</li>
                <li>• <strong>Dati di contatto:</strong> email, telefono, indirizzo</li>
                <li>• <strong>Dati economici:</strong> coordinate bancarie, dati di pagamento</li>
                <li>• <strong>Documenti:</strong> carta d'identità, visura camerale (per agenti)</li>
                <li>• <strong>Dati immobiliari:</strong> informazioni sugli immobili inseriti</li>
              </ul>

              <h3 className="text-xl font-medium text-[#1D1D1F] mb-3">3.2 Dati raccolti automaticamente</h3>
              <ul className="space-y-2 text-[#424245] mb-6">
                <li>• <strong>Dati di navigazione:</strong> indirizzo IP, browser, dispositivo</li>
                <li>• <strong>Dati di utilizzo:</strong> pagine visitate, tempo di permanenza, click</li>
                <li>• <strong>Geolocalizzazione:</strong> posizione approssimativa (città/regione)</li>
                <li>• <strong>Cookie:</strong> come descritto nella <Link to="/cookies" className="text-[#0071e3] hover:underline">Cookie Policy</Link></li>
              </ul>

              <h3 className="text-xl font-medium text-[#1D1D1F] mb-3">3.3 Dati da fonti terze</h3>
              <ul className="space-y-2 text-[#424245]">
                <li>• <strong>Social login:</strong> dati del profilo Google (se utilizzi accesso con Google)</li>
                <li>• <strong>Servizi di verifica:</strong> dati da registri pubblici per verifica identità</li>
              </ul>
            </div>

            {/* Finalità */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">4. Finalità e Base Giuridica del Trattamento</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-[#F5F5F7]">
                      <th className="text-left p-4 font-semibold text-[#1D1D1F]">Finalità</th>
                      <th className="text-left p-4 font-semibold text-[#1D1D1F]">Base Giuridica</th>
                      <th className="text-left p-4 font-semibold text-[#1D1D1F]">Conservazione</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#424245]">
                    <tr className="border-b border-gray-100">
                      <td className="p-4">Erogazione del servizio</td>
                      <td className="p-4">Esecuzione contratto</td>
                      <td className="p-4">Durata rapporto + 10 anni</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="p-4">Verifica identità e antiriciclaggio</td>
                      <td className="p-4">Obbligo legale</td>
                      <td className="p-4">10 anni</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="p-4">Comunicazioni di servizio</td>
                      <td className="p-4">Esecuzione contratto</td>
                      <td className="p-4">Durata rapporto</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="p-4">Marketing diretto</td>
                      <td className="p-4">Consenso</td>
                      <td className="p-4">Fino a revoca consenso</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="p-4">Analisi statistiche</td>
                      <td className="p-4">Legittimo interesse</td>
                      <td className="p-4">26 mesi (dati aggregati)</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="p-4">Sicurezza e prevenzione frodi</td>
                      <td className="p-4">Legittimo interesse</td>
                      <td className="p-4">12 mesi</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="p-4">Adempimenti fiscali</td>
                      <td className="p-4">Obbligo legale</td>
                      <td className="p-4">10 anni</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Destinatari */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">5. Destinatari dei Dati</h2>
              <p className="text-[#424245] leading-relaxed mb-4">
                I tuoi dati personali possono essere comunicati a:
              </p>
              <ul className="space-y-3 text-[#424245]">
                <li>
                  <strong>Fornitori di servizi:</strong> società che forniscono servizi tecnici e operativi 
                  (hosting, email, pagamenti), nominati Responsabili del trattamento ex art. 28 GDPR
                </li>
                <li>
                  <strong>Agenti immobiliari:</strong> professionisti verificati che gestiscono le Gare 
                  di Vendita (solo dati necessari per l'operazione)
                </li>
                <li>
                  <strong>Istituti finanziari:</strong> per la gestione dei pagamenti
                </li>
                <li>
                  <strong>Autorità pubbliche:</strong> quando richiesto per legge
                </li>
                <li>
                  <strong>Consulenti:</strong> legali, fiscali, contabili (sotto vincolo di riservatezza)
                </li>
              </ul>
            </div>

            {/* Trasferimento Extra-UE */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">6. Trasferimento Dati Extra-UE</h2>
              <p className="text-[#424245] leading-relaxed mb-4">
                Alcuni dei nostri fornitori hanno sede al di fuori dell'Unione Europea (es. Google, Stripe). 
                In questi casi, il trasferimento avviene sulla base di:
              </p>
              <ul className="space-y-2 text-[#424245]">
                <li>• Decisioni di adeguatezza della Commissione Europea</li>
                <li>• Clausole Contrattuali Standard (SCC) approvate dalla Commissione</li>
                <li>• Data Privacy Framework UE-USA (per fornitori certificati)</li>
              </ul>
            </div>

            {/* Diritti */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">7. I Tuoi Diritti</h2>
              <p className="text-[#424245] leading-relaxed mb-4">
                Ai sensi degli articoli 15-22 del GDPR, hai diritto di:
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-[#F5F5F7] rounded-xl p-5">
                  <Eye className="w-6 h-6 text-[#0071e3] mb-2" />
                  <h4 className="font-medium text-[#1D1D1F] mb-1">Accesso</h4>
                  <p className="text-sm text-[#424245]">Ottenere conferma del trattamento e copia dei tuoi dati</p>
                </div>
                <div className="bg-[#F5F5F7] rounded-xl p-5">
                  <FileText className="w-6 h-6 text-[#0071e3] mb-2" />
                  <h4 className="font-medium text-[#1D1D1F] mb-1">Rettifica</h4>
                  <p className="text-sm text-[#424245]">Correggere dati inesatti o incompleti</p>
                </div>
                <div className="bg-[#F5F5F7] rounded-xl p-5">
                  <AlertCircle className="w-6 h-6 text-[#0071e3] mb-2" />
                  <h4 className="font-medium text-[#1D1D1F] mb-1">Cancellazione</h4>
                  <p className="text-sm text-[#424245]">Richiedere la cancellazione dei tuoi dati ("diritto all'oblio")</p>
                </div>
                <div className="bg-[#F5F5F7] rounded-xl p-5">
                  <Lock className="w-6 h-6 text-[#0071e3] mb-2" />
                  <h4 className="font-medium text-[#1D1D1F] mb-1">Limitazione</h4>
                  <p className="text-sm text-[#424245]">Limitare il trattamento in determinati casi</p>
                </div>
                <div className="bg-[#F5F5F7] rounded-xl p-5">
                  <Database className="w-6 h-6 text-[#0071e3] mb-2" />
                  <h4 className="font-medium text-[#1D1D1F] mb-1">Portabilità</h4>
                  <p className="text-sm text-[#424245]">Ricevere i tuoi dati in formato strutturato</p>
                </div>
                <div className="bg-[#F5F5F7] rounded-xl p-5">
                  <Shield className="w-6 h-6 text-[#0071e3] mb-2" />
                  <h4 className="font-medium text-[#1D1D1F] mb-1">Opposizione</h4>
                  <p className="text-sm text-[#424245]">Opporti al trattamento per finalità di marketing</p>
                </div>
              </div>

              <div className="mt-6 p-4 border border-[#0071e3]/20 rounded-xl bg-[#0071e3]/5">
                <p className="text-[#424245]">
                  <strong>Come esercitare i tuoi diritti:</strong> Invia una richiesta a{' '}
                  <a href="mailto:privacy@weqasa.it" className="text-[#0071e3] hover:underline">privacy@weqasa.it</a>{' '}
                  o accedi alla sezione "Privacy" nel tuo profilo utente.
                </p>
              </div>
            </div>

            {/* Sicurezza */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">8. Misure di Sicurezza</h2>
              <p className="text-[#424245] leading-relaxed mb-4">
                Adottiamo misure tecniche e organizzative per proteggere i tuoi dati:
              </p>
              <ul className="space-y-2 text-[#424245]">
                <li>• Crittografia SSL/TLS per tutte le comunicazioni</li>
                <li>• Crittografia dei dati sensibili a riposo (AES-256)</li>
                <li>• Autenticazione a due fattori (2FA) disponibile</li>
                <li>• Accesso ai dati limitato al personale autorizzato</li>
                <li>• Backup regolari e disaster recovery</li>
                <li>• Monitoraggio continuo delle vulnerabilità</li>
                <li>• Formazione del personale sulla protezione dati</li>
              </ul>
            </div>

            {/* Minori */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">9. Dati dei Minori</h2>
              <p className="text-[#424245] leading-relaxed">
                Il Servizio non è destinato a minori di 18 anni. Non raccogliamo consapevolmente 
                dati personali di minori. Se veniamo a conoscenza di aver raccolto dati di un minore, 
                provvederemo alla loro cancellazione.
              </p>
            </div>

            {/* Reclamo */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">10. Diritto di Reclamo</h2>
              <p className="text-[#424245] leading-relaxed">
                Se ritieni che il trattamento dei tuoi dati violi il GDPR, hai il diritto di proporre 
                reclamo all'Autorità di controllo competente:
              </p>
              <div className="bg-[#F5F5F7] rounded-xl p-6 mt-4">
                <p className="text-[#424245]">
                  <strong>Garante per la Protezione dei Dati Personali</strong><br />
                  Piazza Venezia 11, 00187 Roma<br />
                  Website: <a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-[#0071e3] hover:underline">www.garanteprivacy.it</a><br />
                  Email: protocollo@gpdp.it
                </p>
              </div>
            </div>

            {/* Contatti */}
            <div className="bg-[#F5F5F7] rounded-2xl p-8">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">11. Contatti</h2>
              <p className="text-[#424245] leading-relaxed mb-4">
                Per qualsiasi domanda sulla presente Privacy Policy:
              </p>
              <div className="space-y-2 text-[#424245]">
                <p><strong>WEQASA S.r.l.</strong></p>
                <p>Via Roma 123, 00100 Roma (RM)</p>
                <p>Email privacy: <a href="mailto:privacy@weqasa.it" className="text-[#0071e3] hover:underline">privacy@weqasa.it</a></p>
                <p>Email DPO: <a href="mailto:dpo@weqasa.it" className="text-[#0071e3] hover:underline">dpo@weqasa.it</a></p>
                <p>PEC: weqasa@pec.it</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
